import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { createPost } from '../../../services/feedServices';

const DefaultProfile = '/images/profile/default-profile.png';

const FeedPost = ({ onPostSubmit, userProfile }) => {
  const [newPost, setNewPost] = useState('');
  const [newPostMedia, setNewPostMedia] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const MAX_CHARACTERS = 1000;

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handleMediaUpload = useCallback((e, type) => {
    const file = e.target.files[0];
    if (file) {
      setNewPostMedia({ type, url: URL.createObjectURL(file), file });
    }
  }, []);

  const handleTextareaChange = useCallback((e) => setNewPost(e.target.value), []);

  const hasContent = useMemo(() => newPost.trim() || newPostMedia, [newPost, newPostMedia]);
  const isPostButtonEnabled = hasContent && !isSubmitting;

  const handleSubmitPost = async () => {
    if (!newPost.trim() && !newPostMedia) return;
    if (newPost.length > MAX_CHARACTERS) return;

    setIsSubmitting(true);
    try {
      const response = await createPost({
        description: newPost.trim(),
        media: newPostMedia
      });

      if (response.success) {
        setNewPost('');
        setNewPostMedia(null);
        setIsExpanded(false);
        if (onPostSubmit) onPostSubmit(response.data.post);
      }
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMediaPreview = (media) => {
    if (media.type === 'image') {
      return (
        <img
          src={media.url}
          alt="Preview"
          className="w-full h-auto max-h-64 object-contain rounded-xl bg-gray-100"
        />
      );
    }
    if (media.type === 'video') {
      return (
        <video src={media.url} controls className="w-full max-h-64 rounded-xl bg-black" />
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {!isExpanded ? (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
        >
          <img
            src={userProfile?.profile_pic_url || DefaultProfile}
            alt=""
            className="w-10 h-10 rounded-full object-cover border-2 border-gray-100 shrink-0"
          />
          <div className="flex-1 px-4 py-2.5 rounded-full bg-gray-100 text-font-secondary text-sm">
            What's on your mind?
          </div>
          <Icon icon="mdi:plus-circle" width="24" height="24" className="text-primary shrink-0" />
        </button>
      ) : (
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-base font-semibold text-font-primary">Create Post</h3>
            <button
              type="button"
              onClick={() => {
                setIsExpanded(false);
                setNewPost('');
                setNewPostMedia(null);
              }}
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
              aria-label="Close"
            >
              <Icon icon="mdi:close" width="20" height="20" />
            </button>
          </div>

          <div className="flex gap-3">
            <img
              src={userProfile?.profile_pic_url || DefaultProfile}
              alt=""
              className="w-10 h-10 rounded-full object-cover border-2 border-gray-100 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <textarea
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none text-font-primary placeholder:text-font-secondary text-sm"
                rows={4}
                placeholder="What's on your mind?"
                value={newPost}
                onChange={handleTextareaChange}
                maxLength={MAX_CHARACTERS}
                autoFocus
              />
              <p className="text-right text-xs text-font-secondary mt-1">
                {newPost.length}/{MAX_CHARACTERS}
              </p>
            </div>
          </div>

          {newPostMedia && (
            <div className="mt-3 relative rounded-lg overflow-hidden bg-gray-50 border border-gray-100">
              {renderMediaPreview(newPostMedia)}
              <button
                type="button"
                onClick={() => setNewPostMedia(null)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                aria-label="Remove media"
              >
                <Icon icon="mdi:close" width="16" height="16" />
              </button>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
            <div className="flex gap-1">
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleMediaUpload(e, 'image')}
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => handleMediaUpload(e, 'video')}
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-font-secondary hover:bg-gray-100 hover:text-primary transition-colors"
              >
                <Icon icon="mdi:image-outline" width="20" height="20" />
                <span className="hidden sm:inline">Photo</span>
              </button>
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-font-secondary hover:bg-gray-100 hover:text-primary transition-colors"
              >
                <Icon icon="mdi:video-outline" width="20" height="20" />
                <span className="hidden sm:inline">Video</span>
              </button>
            </div>
            <button
              type="button"
              onClick={handleSubmitPost}
              disabled={!isPostButtonEnabled}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                  Posting...
                </>
              ) : (
                <>
                  <Icon icon="mdi:send-outline" width="18" height="18" />
                  Post
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedPost;
